import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetchCached } from '../../utils/apiClient';
import './SearchHeroCards.css';
import MsvBrowseShell from '../../components/MsvBrowseShell/MsvBrowseShell';
import VaultHeroFilterForm from '../../components/VaultHeroFilterForm/VaultHeroFilterForm';
import { getVaultHeroBrowseExtras } from '../../components/VaultHeroBrowseExtras/VaultHeroBrowseExtras';
import InfiniteFeed, { FeedSkeleton } from '../../components/InfiniteFeed/InfiniteFeed';
import WishlistHeartButton from '../../components/WishlistHeartButton/WishlistHeartButton';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { buildHeroSearchParams, navigateVaultSearch } from '../../utils/vaultBrowseSearch';

const SearchHeroCards = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchStarted, setSearchStarted] = useState(false);
    const [paginationTokens, setPaginationTokens] = useState({ 1: null });
    const [hasMore, setHasMore] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [sortBy, setSortBy] = useState('recent');

    const [formData, setFormData] = useState({
        driverName: '',
        series: '',
        year: ''
    });

    const [searchResults, setSearchResults] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    const [loadingSearch, setLoadingSearch] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    const [totalHeroCards, setTotalHeroCards] = useState(null);

    useEffect(() => {
        const savedState = sessionStorage.getItem('heroCardsSearchState');
        if (savedState) {
            const {
                formData: savedForm,
                currentPage: savedPage,
                paginationTokens: savedTokens,
                sortBy: savedSortBy
            } = JSON.parse(savedState);

            setFormData(savedForm);
            setCurrentPage(savedPage);
            setPaginationTokens(savedTokens);
            if (savedSortBy === 'oldest' || savedSortBy === 'recent') {
                setSortBy(savedSortBy);
            }
        }

        setSearchStarted(true);
    }, []);

    useEffect(() => {
        const fetchTotalCount = async () => {
            try {
                const response = await apiFetchCached('/Collectibles/GetHomePageData', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    const totalCount = data.sitewideDataResultDto?.numberOfHeroCardsInHeroCardArchive || 0;
                    setTotalHeroCards(totalCount);
                }
            } catch (err) {
                console.error('Failed to fetch total hero cards count:', err);
            }
        };

        fetchTotalCount();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const driver = params.get('driver') || '';
        const seriesParam = params.get('series') || '';
        const year = params.get('year') || '';
        const page = parseInt(params.get('page') || '1', 10);
        const sortParam = params.get('sort');

        setFormData({
            driverName: driver,
            series: seriesParam,
            year
        });
        if (sortParam === 'oldest' || sortParam === 'recent') {
            setSortBy(sortParam);
        }
        setCurrentPage(Number.isFinite(page) && page > 0 ? page : 1);
        setHasMore(true);
        setSearchStarted(true);
    }, [location.search]);

    useEffect(() => {
        if (!searchStarted) return;

        let cancelled = false;

        const fetchData = async () => {
            setLoadingSearch(true);

            try {
                const urlSort = new URLSearchParams(location.search).get('sort');
                const requestSort = urlSort === 'oldest' ? 'oldest' : (sortBy === 'oldest' ? 'oldest' : 'recent');

                const response = await apiFetchCached('/HeroCard/SearchHeroCards', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        DriverName: formData.driverName,
                        Series: formData.series,
                        Year: formData.year,
                        SortBy: requestSort,
                        PageSize: 24,
                        PaginationToken: paginationTokens[currentPage] || null
                    })
                });

                if (cancelled) return;

                const data = await response.json();

                const resultsWithBase64 = (data.heroCardSearchResultDtos || []).map(item => ({
                    ...item,
                    imageUrl: resolveImageSrc(item)
                }));

                setPaginationTokens(prev => ({ ...prev, [currentPage + 1]: data.nextPageToken }));
                setHasMore(!!data.nextPageToken);
                setSearchResults(prev => (currentPage > 1 ? [...prev, ...resultsWithBase64] : resultsWithBase64));
                setApiErrorMessage('');
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setApiErrorMessage('An unexpected error occurred. Please try again.');
                }
            } finally {
                if (!cancelled) {
                    setLoadingSearch(false);
                    setInitialLoad(false);
                    setSearchStarted(false);
                }
            }
        };

        fetchData();

        return () => { cancelled = true; };
    }, [currentPage, formData, sortBy, searchStarted, location.search]);

    useEffect(() => {
        if (!initialLoad && searchResults.length > 0 && currentPage === 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [searchResults, initialLoad, currentPage]);

    const handleFiltersApply = (nextForm) => {
        sessionStorage.removeItem('heroCardsSearchState');
        setPaginationTokens({ 1: null });
        setSearchResults([]);
        setHasMore(true);
        setApiErrorMessage('');

        const params = buildHeroSearchParams(nextForm, { sortBy, type: null });
        navigateVaultSearch(navigate, location, {
            basePath: '/searchHeroCards',
            params,
            onSameUrl: () => {
                setCurrentPage(1);
                setSearchStarted(true);
            },
        });
    };

    const handleSortChange = (nextValue) => {
        const nextSort = nextValue === 'oldest' ? 'oldest' : 'recent';
        sessionStorage.removeItem('heroCardsSearchState');
        setSortBy(nextSort);
        setPaginationTokens({ 1: null });
        setCurrentPage(1);
        setSearchResults([]);
        setHasMore(true);

        const searchParams = new URLSearchParams({
            driver: formData.driverName || '',
            series: formData.series || '',
            year: formData.year || '',
            sort: nextSort,
            page: '1'
        });

        const nextQuery = `?${searchParams.toString()}`;
        if (location.search === nextQuery) {
            setSearchStarted(true);
            return;
        }

        navigate(`/searchHeroCards${nextQuery}`);
    };

    const handleClearFilters = (cleared) => {
        sessionStorage.removeItem('heroCardsSearchState');
        setFormData(cleared);
        setSortBy('recent');
        setPaginationTokens({ 1: null });
        setCurrentPage(1);
        setSearchResults([]);
        setHasMore(true);
        setApiErrorMessage('');

        const searchParams = new URLSearchParams({
            driver: '',
            series: '',
            year: '',
            sort: 'recent',
            page: '1'
        });

        const nextUrl = `/searchHeroCards?${searchParams.toString()}`;
        if (`${location.pathname}${location.search}` === nextUrl ||
            location.search === `?${searchParams.toString()}`) {
            setSearchStarted(true);
            return;
        }

        navigate(nextUrl);
    };

    const handleNavigateToItem = (item) => {
        const stateToSave = {
            formData,
            currentPage,
            paginationTokens,
            searchStarted: true,
            sortBy
        };
        sessionStorage.setItem('heroCardsSearchState', JSON.stringify(stateToSave));
        window.location.href = `/herocard/${item.heroCardId}`;
    };

    const browseExtras = getVaultHeroBrowseExtras({
        formData,
        onFiltersApply: handleFiltersApply,
        onClearFilters: () => handleClearFilters({ driverName: '', series: '', year: '' }),
    });

    return (
        <MsvBrowseShell
            className="search-hero-cards-page"
            eyebrow="Browse THE VAULT"
            title="Search Hero Cards"
            hubVariant="vault"
            sectionTitle="Search Hero Cards"
            resultCount={totalHeroCards != null ? totalHeroCards : (searchResults.length > 0 ? searchResults.length : null)}
            belowToolbar={browseExtras.belowToolbar}
            toolbar={(
                <VaultHeroFilterForm
                    formData={formData}
                    onFiltersApply={handleFiltersApply}
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    onClearFilters={handleClearFilters}
                    lockDriverSeries={false}
                >
                    {apiErrorMessage && (
                        <p className="msv-browse-error" role="alert">{apiErrorMessage}</p>
                    )}
                </VaultHeroFilterForm>
            )}
        >
            {loadingSearch && searchResults.length === 0 ? (
                <FeedSkeleton />
            ) : (
                <InfiniteFeed
                    items={searchResults}
                    getKey={(item, index) => item.heroCardId || item.collectionItemId || index}
                    hasMore={hasMore}
                    loading={loadingSearch && searchResults.length === 0}
                    loadingMore={loadingSearch && searchResults.length > 0}
                    emptyMessage="No Hero Cards Found"
                    onLoadMore={() => {
                        if (!hasMore || loadingSearch) return;
                        setSearchStarted(true);
                        setCurrentPage(prev => prev + 1);
                    }}
                    renderItem={(item) => (
                        <div className="msv-item-card">
                            <div className="msv-item-card-image">
                                <img src={item.imageUrl} alt={item.title} loading="lazy" decoding="async" />
                                <WishlistHeartButton itemType="heroCard" item={item} />
                            </div>
                            <div className="msv-item-card-body">
                                <h4 className="msv-item-card-title">{toTitleCase(item.title)}</h4>
                                <button type="button" onClick={() => handleNavigateToItem(item)} className="msv-item-card-btn">
                                    View
                                </button>
                            </div>
                        </div>
                    )}
                />
            )}
        </MsvBrowseShell>
    );
};

export default SearchHeroCards;
